import type { ScriptBlockData } from '../components/story/ScriptBlock';
import type { BlockType } from '../components/story/SlashMenu';
import { runsFor,deletionMarksFor } from './scriptRichText';
import {dualFor, withDual, type DualParagraph, type ScriptDualDialogue} from './scriptDualDialogue';
import {alternateChoices,validateAlternates,type ScriptAlternates} from './scriptAlternates';
import {validateRevisions,validateRevisionReferences,showAllRevisionText,showAllRevisionMarks,revisionParagraphs,type ScriptRevisions} from './scriptRevisions';

export interface FDXDiagnostic {
    readonly code: string;
    readonly message: string;
}

export class FDXError extends Error {
    constructor(readonly code: string, message: string) {
        super(message);
        this.name = 'FDXError';
    }
}

export interface FDXTextRun {
    readonly text: string;
    readonly attributes: Readonly<Record<string, string>>;
}

export interface FDXParagraph {
    /** ezart identity, independent of Final Draft bookmarks and scene numbers. */
    readonly id: string;
    readonly elementName: string;
    readonly attributes: Readonly<Record<string, string>>;
    readonly runs: readonly FDXTextRun[];
    /** Keep the serialized sequence; arbitrary two-column partitioning is unsafe. */
    readonly dualDialogue?: readonly FDXParagraph[];
    readonly metadataNames: readonly string[];
    readonly alternateIds?: {current:number;ids:readonly string[]};
}

export interface FDXDocument {
    /** Authoritative preservation envelope. Projections below are read-only views.
     * Use replaceFDXTextRun for edits; do not reconstruct XML from editor blocks. */
    readonly sourceXml: string;
    readonly attributes: Readonly<Record<string, string>>;
    readonly paragraphs: readonly FDXParagraph[];
    readonly sectionNames: readonly string[];
    readonly diagnostics: readonly FDXDiagnostic[];
    readonly alternateParagraphs?: Readonly<Record<string,FDXParagraph>>;
}

const nativeTypes: Record<Exclude<BlockType, 'dual-dialogue'>, string> = {
    'scene-heading': 'Scene Heading', action: 'Action', character: 'Character',
    dialogue: 'Dialogue', parenthetical: 'Parenthetical', transition: 'Transition', shot: 'Shot',
};
const editorTypes = new Map(Object.entries(nativeTypes).map(([type, name]) =>
    [name.toLowerCase(), type as BlockType]));
const children = (node: Element, name: string): Element[] =>
    Array.from(node.children).filter(child => child.tagName === name);
const attributes = (node: Element): Readonly<Record<string, string>> =>
    Object.freeze(Object.fromEntries(Array.from(node.attributes).map(attr => [attr.name, attr.value])));

function parse(xml: string): XMLDocument {
    // This adapter preserves serialized XML, not DTD/entity declarations.
    if (/<!DOCTYPE\s/i.test(xml)) throw new FDXError('unsupported-doctype', 'FDX with a DTD is unsupported.');
    const document = new DOMParser().parseFromString(xml, 'application/xml');
    if (document.getElementsByTagName('parsererror').length ||
        document.documentElement?.tagName !== 'FinalDraft' || document.documentElement.namespaceURI) {
        throw new FDXError('invalid-xml', 'Expected a valid, unnamespaced FinalDraft XML document.');
    }
    if (children(document.documentElement, 'Content').length !== 1) {
        throw new FDXError('invalid-content', 'FDX must have exactly one direct screenplay Content section.');
    }
    return document;
}

function readParagraph(node: Element, identity?: FDXParagraph): FDXParagraph {
    const dual = children(node, 'DualDialogue');
    const alts=children(node,'Alts');let alternateIds:FDXParagraph['alternateIds'];
    if(alts.length){
        const current=alts[0].getAttribute('Current'),ids=children(alts[0],'AltId').map(n=>n.textContent??'');
        if(alts.length!==1 || alts[0].attributes.length!==1 || !current || !/^\d+$/.test(current)
            || Number(current)>ids.length || ids.length>99 || new Set(ids).size!==ids.length
            || ids.some(id=>!/^(0|[1-9]\d*)$/.test(id)||Number(id)>0x3ffc) || Array.from(alts[0].children).some(n=>n.tagName!=='AltId'||n.attributes.length||n.children.length))
            throw new FDXError('invalid-alternates','Unsupported alternate selector. Keep the original FDX.');
        alternateIds={current:Number(current),ids};
    }
    if (dual.length > 1) throw new FDXError('invalid-dual-dialogue', 'A paragraph has multiple DualDialogue containers.');
    return Object.freeze({
        id: identity?.id ?? crypto.randomUUID(),
        elementName: node.getAttribute('Type') ?? 'Action',
        attributes: attributes(node),
        runs: Object.freeze(children(node, 'Text').map(run => Object.freeze({
            text: run.textContent ?? '', attributes: attributes(run),
        }))),
        dualDialogue: dual[0] ? Object.freeze(children(dual[0], 'Paragraph').map((paragraph, index) =>
            readParagraph(paragraph, identity?.dualDialogue?.[index]))) : undefined,
        metadataNames: Object.freeze(Array.from(node.children)
            .filter(child => !['Text', 'DualDialogue'].includes(child.tagName)).map(child => child.tagName)),
        alternateIds,
    });
}

/** Reads only /FinalDraft/Content/Paragraph. Notes, omitted scenes, titles,
 * and unknown sections remain in sourceXml without becoming screenplay prose. */
export function importFDXDocument(xml: string): FDXDocument {
    return readDocument(xml);
}

function readDocument(xml: string, identity?: FDXDocument): FDXDocument {
    const root = parse(xml).documentElement;
    const alternateParagraphs:Record<string,FDXParagraph>={};const collections=children(root,'AltCollection');
    if(collections.length>1)throw new FDXError('invalid-alternates','Multiple alternate collections cannot be edited safely.');
    for(const alt of Array.from(collections[0]?.children??[])){
        const id=alt.getAttribute('Id'),parts=children(alt,'Paragraph');
        if(alt.tagName!=='Alt' || !id || !/^(0|[1-9]\d*)$/.test(id) || Number(id)>0x3ffc || Object.hasOwn(alternateParagraphs,id)
            || alt.attributes.length!==1 || parts.length!==1 || alt.children.length!==1)
            throw new FDXError('invalid-alternates','Invalid alternate collection. Keep the original FDX.');
        alternateParagraphs[id]=readParagraph(parts[0]);
    }
    const diagnostics: FDXDiagnostic[] = [];
    if (root.getAttribute('Version') !== '4') diagnostics.push({code: 'unverified-version',
        message: 'Only Version 4 is covered by the installed Final Draft corpus. This version is preserved without interpreting its settings.'});
    if (root.getAttribute('DocumentType') !== 'Script') diagnostics.push({code: 'non-script-document',
        message: 'This is not a Script document. Preserve it as FDX; do not treat it as a screenplay.'});
    return Object.freeze({sourceXml: xml, attributes: attributes(root),
        paragraphs: Object.freeze(children(children(root, 'Content')[0], 'Paragraph')
            .map((node, index) => readParagraph(node, identity?.paragraphs[index]))),
        sectionNames: Object.freeze(Array.from(root.children).map(child => child.tagName)),
        diagnostics: Object.freeze(diagnostics.map(diagnostic => Object.freeze(diagnostic))),
        alternateParagraphs:Object.freeze(alternateParagraphs),
    });
}

/** Returns the exact imported XML string, including opaque sections. */
export function exportFDXDocument(document: FDXDocument): string {
    return document.sourceXml;
}

/** Native definitions are distinct from Text RevisionID membership. Unknown
 * collated/anchored production behavior stays in the original preservation file. */
export function readFDXRevisions(xml:string):ScriptRevisions|undefined {
    const root=parse(xml).documentElement,nodes=children(root,'Revisions');
    if(!nodes.length)return;
    if(nodes.length!==1)throw new FDXError('unsupported-revisions','Multiple revision collections cannot be edited safely.');
    const node=nodes[0],allowed=['ActiveSet','Location','RevisionMode','RevisionsShown','ShowAllMarks','ShowAllSets','ShowPageColor'];
    if(Array.from(node.attributes).some(a=>!allowed.includes(a.name)))throw new FDXError('unsupported-revisions','Unknown revision options remain in the original FDX.');
    if(!node.children.length&&node.getAttribute('RevisionMode')!=='Yes')return;
    const yes=(key:string,fallback=false)=>{const v=node.getAttribute(key);if(v!==null&&!['Yes','No'].includes(v))throw new FDXError('unsupported-revisions','Invalid revision option.');return v===null?fallback:v==='Yes';};
    const shown=node.getAttribute('RevisionsShown')??'Active';
    if(!['Active','Last','All'].includes(shown))throw new FDXError('unsupported-revisions','Unknown revision display remains in the original FDX.');
    const sets=Array.from(node.children).map(s=>{
        if(s.tagName!=='Revision'||s.children.length||Array.from(s.attributes).some(a=>!['ID','Name','Mark','Color','PageColor','Style','FullRevision'].includes(a.name)))throw new FDXError('unsupported-revisions','Unsupported revision definition.');
        const full=s.getAttribute('FullRevision')??'No';if(!['Yes','No'].includes(full))throw new FDXError('unsupported-revisions','Invalid full revision definition.');
        return {id:s.getAttribute('ID')??'',name:s.getAttribute('Name')??'',mark:s.getAttribute('Mark')??'*',color:s.getAttribute('Color')??'#000000000000',pageColor:s.getAttribute('PageColor')??'#FFFFFFFFFFFF',style:s.getAttribute('Style')??'',fullRevision:full==='Yes'};
    });
    const data:ScriptRevisions={schemaVersion:1,activeSet:node.getAttribute('ActiveSet')??'',mode:yes('RevisionMode'),display:shown==='Active'?'active':shown==='Last'?'collated':!yes('ShowAllMarks',true)&&!yes('ShowAllSets',true)?'none':'all',
        showPageColor:yes('ShowPageColor'),showAllText:yes('ShowAllSets',true),showAllMarks:yes('ShowAllMarks',true),markLocation:Number(node.getAttribute('Location')??'7.75'),sets};
    try{validateRevisions(data);}catch{throw new FDXError('unsupported-revisions','Invalid revision definitions remain in the original FDX.');}return data;
}
function writeFDXRevisions(dom:XMLDocument,r?:ScriptRevisions):void {
    if(!r)return;validateRevisions(r);
    children(dom.documentElement,'Revisions').forEach(n=>n.remove());const node=dom.createElement('Revisions');
    const attrs={ActiveSet:r.activeSet,Location:String(r.markLocation),RevisionMode:r.mode?'Yes':'No',RevisionsShown:r.display==='active'?'Active':r.display==='collated'?'Last':'All',ShowAllMarks:r.display!=='none'&&showAllRevisionMarks(r)?'Yes':'No',ShowAllSets:r.display!=='none'&&showAllRevisionText(r)?'Yes':'No',ShowPageColor:r.display!=='none'&&r.showPageColor?'Yes':'No'};
    Object.entries(attrs).forEach(([k,v])=>node.setAttribute(k,v));
    r.sets.forEach(s=>{const child=dom.createElement('Revision');Object.entries({ID:s.id,Name:s.name,Mark:s.mark,Color:s.color,PageColor:s.pageColor,Style:s.style,FullRevision:s.fullRevision?'Yes':'No'}).forEach(([k,v])=>{validXMLText(v);child.setAttribute(k,v);});node.append(child);});dom.documentElement.append(node);
}

function validXMLText(text: string): void {
    for (const character of text) {
        const code = character.codePointAt(0)!;
        if (!(code === 9 || code === 10 || code === 13 || code >= 0x20 && code <= 0xd7ff ||
            code >= 0xe000 && code <= 0xfffd || code >= 0x10000 && code <= 0x10ffff)) {
            throw new FDXError('invalid-xml-character', 'Text contains a character that XML 1.0 cannot represent.');
        }
    }
}

/** Bounded edit: preserves run attributes and opaque XML. Until anchor coordinate
 * systems are known, documents with production positions cannot be text-edited. */
export function replaceFDXTextRun(
    document: FDXDocument, paragraphId: string, runIndex: number, text: string,
): FDXDocument {
    if (document.diagnostics.length) throw new FDXError('unsupported-edit', 'Editing requires a Version 4 Script document.');
    validXMLText(text);
    const dom = parse(document.sourceXml);
    let target: Element | undefined;
    function locate(nodes: Element[], paragraphs: readonly FDXParagraph[]): void {
        paragraphs.forEach((paragraph, index) => {
            if (paragraph.id === paragraphId) target = nodes[index];
            const dual = children(nodes[index], 'DualDialogue')[0];
            if (dual && paragraph.dualDialogue) locate(children(dual, 'Paragraph'), paragraph.dualDialogue);
        });
    }
    locate(children(children(dom.documentElement, 'Content')[0], 'Paragraph'), document.paragraphs);
    const run = target && children(target, 'Text')[runIndex];
    if (!Number.isInteger(runIndex) || !run) throw new FDXError('missing-run', 'The requested paragraph/text run does not exist.');
    if (run.textContent === text) return document;
    if (run.children.length) throw new FDXError('unsupported-run-edit', 'This Text run contains nested XML that cannot be replaced safely.');
    const root = dom.documentElement;
    const positioned = ['LockedPages', 'DeletedText', 'TagData'].some(name =>
        children(root, name).some(section => section.children.length > 0));
    const revisions = children(root, 'Revisions').some(section => section.getAttribute('RevisionMode') === 'Yes');
    const anchoredParagraphData = ['ScriptNote', 'OmittedScene', 'Alts', 'SceneProperties']
        .some(name => root.getElementsByTagName(name).length > 0);
    const markedRuns = Array.from(root.getElementsByTagName('Text')).some(node =>
        node.hasAttribute('TagNumber') || Boolean(node.getAttribute('RevisionID') && node.getAttribute('RevisionID') !== '0'));
    if (positioned || revisions || anchoredParagraphData || markedRuns) {
        throw new FDXError('unsafe-production-edit', 'Text editing is blocked: production anchors, revisions, or scene metadata cannot yet be recalculated safely. The original FDX can still be exported unchanged.');
    }
    run.textContent = text;
    // The browser serializer emits literal CR in text; reparsing normalizes it to
    // LF. Imported CDATA/comments already underwent XML newline normalization,
    // so remaining CR comes from character references or this new text node.
    const xml = new XMLSerializer().serializeToString(dom).replace(/\r/g, '&#13;');
    return readDocument(xml, document);
}

const textOf = (paragraph: FDXParagraph): string => paragraph.runs.map(run => run.text).join('');

/** Explicitly lossy view for the current plain-block editor. Retain the original
 * FDXDocument alongside this projection; diagnostics must be shown before use. */
export function projectFDXToBlocks(document: FDXDocument, richDual = false): {
    blocks: ScriptBlockData[]; diagnostics: FDXDiagnostic[];
} {
    const diagnostics = [...document.diagnostics];
    const root = parse(document.sourceXml).documentElement;
    const content = children(root, 'Content')[0];
    if (Array.from(root.getElementsByTagName('Text')).some(node => node.children.length > 0)) {
        diagnostics.push({code: 'nested-run-xml-not-in-editor', message: 'Nested XML inside a Text run cannot be represented by the plain editor.'});
    }
    if (root.attributes.length > 3 || Array.from(root.attributes).some(attr =>
        !['DocumentType', 'Template', 'Version'].includes(attr.name)) ||
        root.getAttribute('Template') !== 'No' || document.sectionNames.some(name => name !== 'Content') ||
        content.attributes.length || Array.from(content.children).some(node => node.tagName !== 'Paragraph')) {
        diagnostics.push({code: 'document-metadata-not-in-editor', message: 'The plain editor view does not contain all document settings, title pages, or production metadata. Keep the original FDX document.'});
    }
    function check(paragraph: FDXParagraph): void {
        if (paragraph.runs.length !== 1 || paragraph.runs.some(run => Object.keys(run.attributes).length > 0) ||
            paragraph.metadataNames.length || Object.keys(paragraph.attributes).some(name => name !== 'Type')) {
            diagnostics.push({code: 'paragraph-metadata-not-in-editor', message: `Paragraph ${paragraph.id} has formatting or metadata that the plain editor view cannot preserve.`});
        }
        paragraph.dualDialogue?.forEach(check);
    }
    const blocks = document.paragraphs.map(paragraph => {
        check(paragraph);
        if (paragraph.dualDialogue) {
            const parts = paragraph.dualDialogue;
            if (richDual) {
                const cues=parts.flatMap((part,index) => part.elementName==='Character' ? [index] : []);
                if (cues.length<2 || cues[0]!==0 || textOf(paragraph).trim() || parts.some(part => part.dualDialogue
                    || !['Character','Dialogue','Parenthetical'].includes(part.elementName))) {
                    throw new FDXError('unsupported-editor-dual','Dual dialogue needs two unambiguous character-led streams of dialogue and parentheticals. Keep the original FDX.');
                }
                const convert=(part:FDXParagraph):DualParagraph => ({id:part.id,type:editorTypes.get(part.elementName.toLowerCase()) as DualParagraph['type'],
                    content:textOf(part),richText:{schemaVersion:1,paragraphAttributes:{...part.attributes},runs:part.runs.map(run => ({text:run.text,attributes:{...run.attributes}}))}});
                const data:ScriptDualDialogue={schemaVersion:1,paragraphAttributes:{...paragraph.attributes},left:parts.slice(0,cues[1]).map(convert),right:parts.slice(cues[1]).map(convert)};
                try {return withDual({id:paragraph.id,type:'dual-dialogue',content:''},data);}
                catch {throw new FDXError('unsupported-editor-dual','Each dual stream needs a character cue and dialogue. Keep the original FDX.');}
            }
            if (parts.length !== 4 || parts.some((part, index) => part.dualDialogue ||
                part.elementName !== (index % 2 === 0 ? 'Character' : 'Dialogue')) || textOf(paragraph).trim()) {
                throw new FDXError('unsupported-editor-dual', 'The current editor can only represent dual dialogue with one cue and one speech per side. Keep this document in structured FDX form.');
            }
            return {id: paragraph.id, type: 'dual-dialogue' as const, content: JSON.stringify({
                left: {character: textOf(parts[0]), dialogue: textOf(parts[1])},
                right: {character: textOf(parts[2]), dialogue: textOf(parts[3])},
            })};
        }
        const type = editorTypes.get(paragraph.elementName.toLowerCase());
        if (!type) throw new FDXError('unsupported-editor-element', `The current editor cannot represent the FDX element ${paragraph.elementName}.`);
        return {id: paragraph.id, type, content: textOf(paragraph)};
    });
    return {blocks, diagnostics};
}

/** Compatibility entry point for plain documents only. Rich documents require
 * importFDXDocument and the explicit diagnostic-bearing projection above. */
export function importFDX(xml: string): ScriptBlockData[] {
    const result = projectFDXToBlocks(importFDXDocument(xml));
    if (result.diagnostics.length) throw new FDXError('lossy-editor-import', result.diagnostics.map(item => item.message).join('\n'));
    return result.blocks;
}

const escapeXML = (text: string): string => {
    validXMLText(text);
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&apos;').replace(/\r/g, '&#13;');
};
const paragraphXML = (type: string, text: string): string =>
    `<Paragraph Type="${type}"><Text>${escapeXML(text)}</Text></Paragraph>`;

function dualXML(content: string): string {
    let parsed: unknown;
    try { parsed = JSON.parse(content); } catch { throw new FDXError('invalid-dual-content', 'Dual dialogue must contain valid structured JSON.'); }
    const record = (value: unknown): Record<string, unknown> => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) throw new FDXError('invalid-dual-content', 'Dual dialogue must have two character/dialogue objects.');
        return value as Record<string, unknown>;
    };
    const data = record(parsed);
    if (Object.keys(data).some(key => !['left', 'right', 'LEFT', 'RIGHT'].includes(key)) ||
        ('left' in data && 'LEFT' in data) || ('right' in data && 'RIGHT' in data)) {
        throw new FDXError('invalid-dual-content', 'Dual dialogue has unsupported or conflicting fields.');
    }
    const sides = [record(data.left ?? data.LEFT), record(data.right ?? data.RIGHT)];
    const paragraphs = sides.map(side => {
        if (Object.keys(side).some(key => !['character', 'dialogue', 'CHARACTER', 'DIALOGUE'].includes(key)) ||
            ('character' in side && 'CHARACTER' in side) || ('dialogue' in side && 'DIALOGUE' in side)) {
            throw new FDXError('invalid-dual-content', 'Dual dialogue has unsupported or conflicting fields.');
        }
        const character = side.character ?? side.CHARACTER;
        const dialogue = side.dialogue ?? side.DIALOGUE;
        if (typeof character !== 'string' || typeof dialogue !== 'string') throw new FDXError('invalid-dual-content', 'Dual character and dialogue values must be strings.');
        return paragraphXML('Character', character) + paragraphXML('Dialogue', dialogue);
    });
    return `<Paragraph Type="General"><DualDialogue>${paragraphs.join('')}</DualDialogue><Text></Text></Paragraph>`;
}

/** Exports the current editor subset. For imported rich documents, use
 * exportFDXDocument so production metadata is not discarded. */
export function exportFDX(blocks: readonly ScriptBlockData[]): string {
    if(blocks.some(b=>b.alternates || b.dualDialogue && [...b.dualDialogue.left,...b.dualDialogue.right].some(p=>p.alternates)))return exportEditedFDX(blocks);
    const body = blocks.map(block => {
        if (block.type === 'dual-dialogue') {
            if (!block.dualDialogue) return dualXML(block.content);
            const data=dualFor(block);
            return `<Paragraph Type="General"><DualDialogue>${[...data.left,...data.right].map(p => paragraphXML(nativeTypes[p.type],p.content)).join('')}</DualDialogue><Text/></Paragraph>`;
        }
        const type = Object.hasOwn(nativeTypes, block.type) ? nativeTypes[block.type] : undefined;
        if (!type) throw new FDXError('unsupported-editor-element', `Unknown editor element ${block.type}.`);
        return paragraphXML(type, block.content);
    }).join('\n');
    return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n<FinalDraft DocumentType="Script" Template="No" Version="4"><Content>\n${body}\n</Content></FinalDraft>`;
}

/** Native paragraph attributes and ordered runs become persistent editor data.
 * Dual dialogue becomes ordered, individually styled paragraphs per stream. */
export function projectFDXToRichBlocks(document: FDXDocument): ReturnType<typeof projectFDXToBlocks>&{revisions?:ScriptRevisions} {
    const result = projectFDXToBlocks(document,true);
    const root = parse(document.sourceXml).documentElement;
    if (Array.from(root.getElementsByTagName('Text')).some(node => node.children.length)) {
        throw new FDXError('unsupported-run-edit', 'Nested XML in a Text run cannot yet be edited. Keep the original FDX.');
    }
    if (Array.from(root.getElementsByTagName('DualDialogue')).some(node => node.attributes.length || Array.from(node.children).some(child => child.tagName!=='Paragraph'))) {
        throw new FDXError('unsupported-editor-dual','Unknown dual-dialogue container data cannot be edited safely. Keep the original FDX.');
    }
    result.blocks = result.blocks.map((block, index) => block.type === 'dual-dialogue' ? block : ({...block,
        richText: {schemaVersion: 1, paragraphAttributes: {...document.paragraphs[index].attributes},
            runs: document.paragraphs[index].runs.map(run => ({text: run.text, attributes: {...run.attributes}}))}}));
    const readAlternates=(p:FDXParagraph):ScriptAlternates|undefined=>{
        if(!p.alternateIds)return undefined;
        const inactive=p.alternateIds.ids.map(id=>{
            const alt=document.alternateParagraphs?.[id];
            if(!alt || alt.attributes.Type && alt.elementName!==p.elementName || alt.dualDialogue || alt.metadataNames.length)
                throw new FDXError('invalid-alternates','Missing, conflicting or unsupported alternate paragraph data. Keep the original FDX.');
            return {id:crypto.randomUUID(),content:textOf(alt),richText:{schemaVersion:1 as const,paragraphAttributes:{...alt.attributes},runs:alt.runs.map(r=>({text:r.text,attributes:{...r.attributes}}))}};
        });
        const activeId=crypto.randomUUID(),order=inactive.map(c=>c.id);order.splice(p.alternateIds.current,0,activeId);
        return {schemaVersion:1,activeId,order,inactive};
    };
    result.blocks=result.blocks.map((block,i)=>{
        const p=document.paragraphs[i];
        if(block.dualDialogue){
            if(p.alternateIds)throw new FDXError('invalid-alternates','Alternatives on an outer dual container cannot be edited safely.');
            const parts=p.dualDialogue!;let index=0;
            const side=(items:DualParagraph[])=>items.map(item=>({...item,alternates:readAlternates(parts[index++])}));
            return withDual(block,{...block.dualDialogue,left:side(block.dualDialogue.left),right:side(block.dualDialogue.right)});
        }
        return {...block,alternates:readAlternates(p)};
    });
    let revisions:ScriptRevisions|undefined;
    try{revisions=readFDXRevisions(document.sourceXml);}catch(error){result.diagnostics.push({code:'revision-settings-not-in-editor',message:error instanceof Error?error.message:'Revision settings remain in the original FDX.'});}
    try{validateRevisionReferences(result.blocks,revisions);}catch{throw new FDXError('unsupported-revisions','Text refers to a missing revision definition. Keep the original FDX.');}
    return {...result,revisions};
}

interface AltWriter {used:Set<number>;records:Element[]}
function writeRichParagraph(dom: XMLDocument, block: ScriptBlockData, original?: Element,alts?:AltWriter): Element {
    if (block.type === 'dual-dialogue') {
        const data=dualFor(block);
        const paragraph=original?.cloneNode(true) as Element | undefined ?? dom.createElement('Paragraph');
        // Existing safe, empty outer Text nodes stay attached; only the stream
        // container is replaced. The root export guard checks production data.
        for (const attr of Array.from(paragraph.attributes)) paragraph.removeAttribute(attr.name);
        for (const [key,value] of Object.entries(data.paragraphAttributes)) {validXMLText(value);paragraph.setAttribute(key,value);}
        if (!paragraph.hasAttribute('Type')) paragraph.setAttribute('Type','General');
        children(paragraph,'DualDialogue').forEach(node => node.remove());
        const dual=dom.createElement('DualDialogue');
        [...data.left,...data.right].forEach(part => dual.append(writeRichParagraph(dom,part,undefined,alts)));
        paragraph.prepend(dual);
        if (!children(paragraph,'Text').length) paragraph.append(dom.createElement('Text'));
        return paragraph;
    }
    const type = nativeTypes[block.type];
    if (!type) throw new FDXError('unsupported-editor-element', 'Unknown screenplay element.');
    const paragraph = original?.cloneNode(true) as Element | undefined ?? dom.createElement('Paragraph');
    for (const attr of Array.from(paragraph.attributes)) paragraph.removeAttribute(attr.name);
    for (const [key, value] of Object.entries(block.richText?.paragraphAttributes ?? {})) {validXMLText(value); paragraph.setAttribute(key, value);}
    paragraph.setAttribute('Type', type);
    children(paragraph,'DualDialogue').forEach(node => node.remove());
    children(paragraph, 'Text').forEach(run => run.remove());
    children(paragraph,'Alts').forEach(node=>node.remove());
    const fragment = dom.createDocumentFragment();
    for (const run of runsFor(block)) {
        validXMLText(run.text);
        const text = dom.createElement('Text');
        for (const [key, value] of Object.entries(run.attributes)) {validXMLText(value); text.setAttribute(key, value);}
        text.textContent = run.text; fragment.append(text);
    }
    if (!fragment.childNodes.length) fragment.append(dom.createElement('Text'));
    paragraph.prepend(fragment);
    if(block.alternates){
        validateAlternates(block.alternates);if(!alts)throw new FDXError('invalid-alternates','Alternate export context is missing.');
        const data=block.alternates,selector=dom.createElement('Alts');selector.setAttribute('Current',String(data.order.indexOf(data.activeId)));
        for(const choice of alternateChoices(block).filter(c=>c.id!==data.activeId)){
            let key=0;while(alts.used.has(key)&&key<=0x3ffc)key++;
            if(key>0x3ffc)throw new FDXError('invalid-alternates','Final Draft alternate IDs are exhausted. Keep the recovery draft.');
            alts.used.add(key);const id=String(key),record=dom.createElement('Alt');record.setAttribute('Id',id);
            const alt=writeRichParagraph(dom,{...choice,type:block.type});alt.removeAttribute('Type');record.append(alt);alts.records.push(record);
            const ref=dom.createElement('AltId');ref.textContent=id;selector.append(ref);
        }
        paragraph.append(selector);
    }
    return paragraph;
}

function requireSafeEditedExport(root: Element): void {
    if (Array.from(root.attributes).some(attr => !['DocumentType', 'Template', 'Version'].includes(attr.name))) throw new FDXError('unsafe-production-edit', 'Unsupported document attributes prevent edited FDX export.');
    const safeSections = new Set(['Content', 'TitlePage', 'PageLayout', 'ElementSettings', 'SmartType', 'HeaderAndFooter','AltCollection','Revisions']);
    for (const section of Array.from(root.children)) {
        if (safeSections.has(section.tagName)) continue;
        if (['LockedPages', 'DeletedText', 'TagData'].includes(section.tagName) && !section.children.length) continue;
        if (section.tagName === 'Revisions' && !section.children.length && section.getAttribute('RevisionMode') !== 'Yes') continue;
        throw new FDXError('unsafe-production-edit', `Edited FDX export is blocked by ${section.tagName}. Its references cannot yet be updated safely. Download the original FDX.`);
    }
    const content = children(root, 'Content')[0];
    if (Array.from(content.children).some(node => node.tagName !== 'Paragraph') || content.attributes.length) {
        throw new FDXError('unsafe-production-edit', 'Unsupported body metadata prevents edited FDX export.');
    }
    readDocument(new XMLSerializer().serializeToString(root));
    let revisionData:ScriptRevisions|undefined;
    try{revisionData=readFDXRevisions(new XMLSerializer().serializeToString(root));}catch{throw new FDXError('unsafe-production-edit','Unsupported revision options prevent edited export. Keep the original FDX.');}
    const collection=children(root,'AltCollection')[0];
    if(collection?.attributes.length)throw new FDXError('unsafe-production-edit','Unsupported alternate collection attributes prevent export.');
    const paragraphs = [...Array.from(content.getElementsByTagName('Paragraph')),...Array.from(collection?.getElementsByTagName('Paragraph')??[])];
    if (Array.from(content.getElementsByTagName('DualDialogue')).some(node => node.attributes.length || Array.from(node.children).some(child => child.tagName!=='Paragraph'))) {
        throw new FDXError('unsafe-production-edit','Unsupported dual-dialogue container metadata prevents edited FDX export.');
    }
    if (paragraphs.some(node => Array.from(node.children).some(child => !['Text', 'DualDialogue','Alts'].includes(child.tagName)))) {
        throw new FDXError('unsafe-production-edit', 'Paragraph notes, alternates, omitted scenes, or scene metadata prevent edited FDX export. Download the original FDX.');
    }
    if (paragraphs.some(node => Array.from(node.attributes).some(attr => !['Type', 'Number', 'Bookmark', 'Label', 'Alignment', 'FirstIndent', 'LeftIndent', 'RightIndent', 'SpaceBefore', 'SpaceAfter', 'StartsNewPage'].includes(attr.name)))) {
        throw new FDXError('unsafe-production-edit', 'Unsupported paragraph attributes prevent edited FDX export.');
    }
    if (paragraphs.flatMap(p=>children(p,'Text')).some(node => node.children.length || Array.from(node.attributes).some(attr =>
        !['Font', 'Style', 'Size', 'Color', 'Background', 'AdornmentStyle', 'RevisionID'].includes(attr.name)
          || attr.name === 'RevisionID' && attr.value !== '0' && !revisionData?.sets.some(s=>s.id===attr.value)))) {
        throw new FDXError('unsafe-production-edit', 'Text anchors, tags, or revisions prevent edited FDX export. Download the original FDX.');
    }
}

export interface FDXSourceBinding { xml: string; paragraphIds?: readonly string[] }
/** Bindings are persisted at import, independent of reparsing-generated UUIDs.
 * The original XML stays immutable. Unknown/positioned production data is never
 * silently stripped to make an export succeed. */
export function exportEditedFDX(blocks: readonly ScriptBlockData[], source?: FDXSourceBinding,revisionData?:ScriptRevisions): string {
    if(revisionParagraphs(blocks).some(p=>deletionMarksFor(p).length))throw new FDXError('unsupported-deletion-export','Story deletion marks cannot yet be translated to Final Draft offsets. Save a snapshot, then clear deletion marks before edited FDX export, or download the original FDX. Preview and PDF retain the marks.');
    const ids = new Set(blocks.map(block => block.id));
    if (ids.size !== blocks.length) throw new FDXError('invalid-identity', 'Duplicate paragraph identities prevent FDX export.');
    if (!source) {
        validateRevisionReferences(blocks,revisionData);
        const dom = parse('<FinalDraft DocumentType="Script" Template="No" Version="4"><Content/></FinalDraft>');
        const content = children(dom.documentElement, 'Content')[0];
        const alts:AltWriter={used:new Set(),records:[]};blocks.forEach(block => content.append(writeRichParagraph(dom, block,undefined,alts)));
        if(alts.records.length){const collection=dom.createElement('AltCollection');collection.append(...alts.records);dom.documentElement.append(collection);}
        writeFDXRevisions(dom,revisionData);
        requireSafeEditedExport(dom.documentElement);
        return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(dom).replace(/\r/g, '&#13;');
    }
    const parsed = importFDXDocument(source.xml), binding = source.paragraphIds;
    if (parsed.diagnostics.length || !binding || binding.length !== parsed.paragraphs.length
        || new Set(binding).size !== binding.length || binding.some(id => typeof id !== 'string' || !id)) {
        throw new FDXError('missing-source-binding', 'This original FDX has no verified paragraph bindings. Reimport it before exporting edits. The original remains available.');
    }
    const projection=projectFDXToRichBlocks(parsed),baseline = projection.blocks.map((block, index) => ({...block, id: binding[index]}));
    const finalRevisions=revisionData??projection.revisions;
    validateRevisionReferences(blocks,finalRevisions);
    const fingerprint=(r?:ScriptRevisions)=>r?JSON.stringify([r.activeSet,r.mode,r.display,r.showPageColor,showAllRevisionText(r),showAllRevisionMarks(r),r.markLocation,r.sets.map(s=>[s.id,s.name,s.mark,s.color,s.pageColor,s.style,s.fullRevision])]):'';
    const revisionChanged=fingerprint(finalRevisions)!==fingerprint(projection.revisions);
    const sameRichText = (a: ScriptBlockData, b: ScriptBlockData): boolean => {
        const sameAttributes = (x: Readonly<Record<string, string>>, y: Readonly<Record<string, string>>) =>
            Object.keys(x).length === Object.keys(y).length && Object.keys(x).every(key => x[key] === y[key]);
        const ar = runsFor(a), br = runsFor(b);
        return sameAttributes(a.richText?.paragraphAttributes ?? {}, b.richText?.paragraphAttributes ?? {})
            && ar.length === br.length && ar.every((run, i) => run.text === br[i].text && sameAttributes(run.attributes, br[i].attributes));
    };
    const sameAlternates=(a:ScriptBlockData,b:ScriptBlockData):boolean=>{
        const x=a.alternates,y=b.alternates;if(!x||!y)return !x&&!y;
        const ax=alternateChoices(a),by=alternateChoices(b);
        return x.order.indexOf(x.activeId)===y.order.indexOf(y.activeId) && ax.length===by.length
            && ax.every((p,i)=>p.content===by[i].content&&sameRichText({...p,type:a.type},{...by[i],type:b.type}));
    };
    const sameDual = (a:ScriptBlockData,b:ScriptBlockData):boolean => {
        const x=dualFor(a),y=dualFor(b);
        if (!a.dualDialogue) return (['left','right'] as const).every(side =>
            x[side].length===y[side].length && x[side].every((p,i) => p.type===y[side][i].type && p.content===y[side][i].content));
        const attrs=(v:Record<string,string>,w:Record<string,string>) => Object.keys(v).length===Object.keys(w).length && Object.keys(v).every(k => v[k]===w[k]);
        return attrs(x.paragraphAttributes,y.paragraphAttributes) && (['left','right'] as const).every(side =>
            x[side].length===y[side].length && x[side].every((p,i) => p.type===y[side][i].type && p.content===y[side][i].content && sameRichText(p,y[side][i])&&sameAlternates(p,y[side][i])));
    };
    const sameBlock = (a: ScriptBlockData, b: ScriptBlockData) => a.id === b.id && a.type === b.type
        && (a.type==='dual-dialogue' ? sameDual(a,b) : a.content === b.content && sameRichText(a, b)&&sameAlternates(a,b));
    if (!revisionChanged && blocks.length === baseline.length && blocks.every((block, index) => sameBlock(block, baseline[index]))) return source.xml;
    const dom = parse(source.xml), root = dom.documentElement;
    requireSafeEditedExport(root);
    const content = children(root, 'Content')[0], original = children(content, 'Paragraph');
    const byId = new Map(binding.map((id, index) => [id, original[index]]));
    const alts:AltWriter={used:new Set(Object.keys(parsed.alternateParagraphs??{}).map(Number)),records:[]};
    // Reuse unchanged imported streams exactly, including safe outer Text nodes.
    const nodes = blocks.map(block => {
        const index = binding.indexOf(block.id);
        if (index >= 0 && block.type === 'dual-dialogue' && sameBlock(block, baseline[index])) return original[index].cloneNode(true);
        if (index >= 0 && block.type === 'dual-dialogue' && baseline[index].type==='dual-dialogue'
            && (!block.dualDialogue || [...block.dualDialogue.left,...block.dualDialogue.right].some(p =>
                p.id===`${block.id}:left:character` || p.id===`${block.id}:right:character`))) {
            throw new FDXError('legacy-dual-formatting', 'This older dual-dialogue import has no verified nested formatting. Reimport its original FDX before exporting dialogue edits. The original remains available.');
        }
        return writeRichParagraph(dom, block, byId.get(block.id),alts);
    });
    original.forEach(node => node.remove()); nodes.forEach(node => content.append(node));
    if(alts.records.length){let collection=children(root,'AltCollection')[0];if(!collection){collection=dom.createElement('AltCollection');root.append(collection);}collection.append(...alts.records);}
    if(revisionChanged)writeFDXRevisions(dom,finalRevisions);
    requireSafeEditedExport(root);
    return new XMLSerializer().serializeToString(dom).replace(/\r/g, '&#13;');
}
